import PropTypes from "prop-types";
import { Tooltip, Icon } from "@mui/material";
import { useICCCColors } from "../components/theme";
import { categoryMeta } from "../model/constants";

// Coordinates are normalised percentages (0-100) of the background image's
// rendered box, so the marker stays anchored across zoom/pan and screen sizes.
export default function DeviceMarker({ device, editable, onClick, onDragEnd, scale }) {
  const ICCC_COLORS = useICCCColors();
  const meta = categoryMeta(device.category);
  const color = ICCC_COLORS[device.health] || ICCC_COLORS.unknown;

  const handlePointerDown = (e) => {
    // Always stop propagation — otherwise the parent canvas's pan handler
    // hijacks the click/drag before it reaches this marker.
    e.stopPropagation();
    if (!editable) return;
    e.preventDefault();
    const marker = e.currentTarget;
    const canvasBox = marker.parentElement.getBoundingClientRect();
    marker.setPointerCapture(e.pointerId);

    const move = (ev) => {
      const xPct = ((ev.clientX - canvasBox.left) / canvasBox.width) * 100;
      const yPct = ((ev.clientY - canvasBox.top) / canvasBox.height) * 100;
      marker.style.left = `${Math.min(100, Math.max(0, xPct))}%`;
      marker.style.top = `${Math.min(100, Math.max(0, yPct))}%`;
    };

    const up = (ev) => {
      marker.releasePointerCapture(e.pointerId);
      marker.removeEventListener("pointermove", move);
      marker.removeEventListener("pointerup", up);
      const xPct = ((ev.clientX - canvasBox.left) / canvasBox.width) * 100;
      const yPct = ((ev.clientY - canvasBox.top) / canvasBox.height) * 100;
      onDragEnd(device, Math.min(100, Math.max(0, xPct)), Math.min(100, Math.max(0, yPct)));
    };

    marker.addEventListener("pointermove", move);
    marker.addEventListener("pointerup", up);
  };

  return (
    <Tooltip title={`${device.nodeName || `Asset #${device.id}`} — ${meta.label}`}>
      <div
        onPointerDown={handlePointerDown}
        onClick={(e) => {
          e.stopPropagation();
          if (!editable) onClick(device);
        }}
        style={{
          position: "absolute",
          left: `${device.xCoordinate}%`,
          top: `${device.yCoordinate}%`,
          transform: `translate(-50%, -50%) scale(${1 / scale})`,
          transformOrigin: "center",
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: color,
          border: "2px solid #fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: editable ? "grab" : "pointer",
          touchAction: "none",
          zIndex: 2,
        }}
      >
        <Icon sx={{ color: "#fff", fontSize: 16 }}>{meta.icon}</Icon>
      </div>
    </Tooltip>
  );
}

DeviceMarker.propTypes = {
  device: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  onClick: PropTypes.func,
  onDragEnd: PropTypes.func,
  scale: PropTypes.number,
};

DeviceMarker.defaultProps = {
  editable: false,
  onClick: () => {},
  onDragEnd: () => {},
  scale: 1,
};
